import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { Card } from "../../UI/Card.tsx";
import { ScreenTitleBlock } from "../../UI/Layout/ScreenTitleBlock.tsx";
import { useUsersEligibleForWarranty } from "./useUsersEligibleForWarranty.ts";
import { UserTable } from "../UserTable.tsx";
import { UserFragmentFragment } from "../../gql/graphql.ts";
import { WeekInput, Interval } from "@normasteaching/norma-ui";
import dayjs from "dayjs";
import { InputWithLabel } from "../../UI/InputWithLabel.tsx";
import { useFilterController } from "../../QueryFilterUtils/useFilterController.ts";
import { useState } from "react";
import { UserDetailModal } from "../UserDetailModal.tsx";
import { useSendResetPasswordEmail } from "../useSendResetPasswordEmail.ts";
import { useToCreateClassRoomPlanScreen } from "../ClassRoomPlan/useToCreateClassRoomPlanScreen.ts";
import { useToUpdateClassRoomPlanScreen } from "../ClassRoomPlan/useToUpdateClassRoomPlanScreen.ts";

export const UsersEligibleForWarrantyScreenPath =
  "/users/eligible-for-warranty";

type PlanEndFilter = {
  fromDate?: string;
  toDate?: string;
};

function filterToInterval(
  filter: PlanEndFilter,
  defaultInterval: Interval,
): Interval {
  if (!filter.fromDate || !filter.toDate) return defaultInterval;
  return new Interval(dayjs(filter.fromDate), dayjs(filter.toDate));
}

export function UsersEligibleForWarrantyScreen() {
  const { filter, setFilter } = useFilterController<PlanEndFilter>();
  const { loading, users } = useUsersEligibleForWarranty({
    plansEndAfter: dayjs(filter.fromDate),
    plansEndBefore: dayjs(filter.toDate),
  });
  const [selectedUser, setSelectedUser] = useState<UserFragmentFragment | null>(
    null,
  );

  function createDefaultInterval(): Interval {
    const now = dayjs();
    return new Interval(now.subtract(1, "month"), now);
  }

  const { sendResetPasswordEmail, loading: sendingResetPasswordEmail } =
    useSendResetPasswordEmail();
  const { toCreateClassRoomPlan } = useToCreateClassRoomPlanScreen();
  const { toUpdatePlanScreen } = useToUpdateClassRoomPlanScreen();

  const titleParts = [`Users eligible for warranty`];
  if (!loading) titleParts.push(`(${users.length})`);

  return (
    <Layout>
      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          sendResetPasswordEmail={async (user) => {
            await sendResetPasswordEmail(user.id);
          }}
          sendingResetPasswordEmail={sendingResetPasswordEmail}
          onNavigateToCreateClassRoomPlan={(userId) => {
            setSelectedUser(null);
            toCreateClassRoomPlan(userId);
          }}
          onNavigateToEditClassRoomPlan={(userId, planId) => {
            setSelectedUser(null);
            toUpdatePlanScreen({ userId, planId });
          }}
        />
      )}
      <Card>
        <ScreenTitleBlock title={titleParts.join(" ")} />
      </Card>
      <Card>
        <div>
          <div className={"mb-4 flex space-x-2"}>
            <div className={"w-full"}>
              <InputWithLabel label={"Plan end date between"}>
                <WeekInput
                  value={filterToInterval(filter, createDefaultInterval())}
                  onChange={(v) =>
                    setFilter({
                      fromDate: v.fromDate.toISOString(),
                      toDate: v.toDate.toISOString(),
                    })
                  }
                />
              </InputWithLabel>
            </div>
          </div>
          {loading ? (
            <LoadingRows />
          ) : (
            <UserTable
              enableCsvExport
              users={users}
              onRowClick={setSelectedUser}
            />
          )}
        </div>
      </Card>
    </Layout>
  );
}
